import { Button } from "./ui/button";
import { ArrowRight, User } from "lucide-react";
import { ImageWithFallback } from "./figma/ImageWithFallback";
import { motion } from "framer-motion";
import { useAuth } from "../lib/AuthContext";

interface HeroProps {
  onNavigate?: (page: string) => void;
}

export function Hero({ onNavigate }: HeroProps = {}) {
  const { user } = useAuth();
  
  const handleGetStarted = () => {
    if (onNavigate) {
      onNavigate("signup");
    } else {
      window.location.hash = "#/signup";
    }
  };

  const handleGoToAccount = () => {
    if (onNavigate) {
      onNavigate("account");
    } else {
      window.location.hash = "#/account";
    }
  };

  const handleExploreReviews = () => {
    if (onNavigate) {
      onNavigate("discover");
    } else {
      window.location.hash = "#/discover";
    }
  };
  return (
    <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <motion.div 
            className="space-y-8"
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            <motion.div 
              className="space-y-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
            >
              <h1 className="text-5xl sm:text-6xl lg:text-7xl tracking-tight">
                Cannabis, rated
                <span className="block text-primary">by patients.</span>
              </h1>
              
              <p className="text-xl text-muted-foreground max-w-lg">
                Real-world reviews of prescribed products in the UK. No pricing. No medical advice. No Bias.
              </p>
            </motion.div>

            <motion.div 
              className="flex flex-col sm:flex-row gap-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.6 }}
            >
              {user ? (
                <Button size="lg" className="gap-2 group" onClick={handleGoToAccount}>
                  <User className="w-5 h-5" />
                  My Account
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              ) : (
                <Button size="lg" className="gap-2 group" onClick={handleGetStarted}>
                  Get Started
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              )}
              <Button size="lg" variant="outline" className="gap-2" onClick={handleExploreReviews}>
                Explore Reviews
              </Button>
            </motion.div>

            {/* Reassurance Band */}
            <motion.div 
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 pt-8 border-t border-border"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.6 }}
            >
              <div className="flex flex-col gap-1">
                <p className="text-sm text-foreground">Safe & verified reviews</p>
              </div>
              <div className="flex flex-col gap-1">
                <p className="text-sm text-foreground">Privacy-first mindset</p>
              </div>
              <div className="flex flex-col gap-1">
                <p className="text-sm text-foreground">Batch-level insights</p>
              </div>
              <div className="flex flex-col gap-1">
                <p className="text-sm text-foreground">Informative & moderated</p>
              </div>
              <div className="flex flex-col gap-1">
                <p className="text-sm text-foreground">Independent & patient owned</p>
              </div>
            </motion.div>
          </motion.div>

          {/* Right Image */}
          <motion.div 
            className="relative"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <div className="relative rounded-3xl overflow-hidden shadow-2xl">
              <ImageWithFallback
                src="https://images.unsplash.com/photo-1630627204838-8a5d7bac81fd?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxjYW5uYWJpcyUyMGxlYWYlMjBtaW5pbWFsfGVufDF8fHx8MTc2MDA4NDYwMHww&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral"
                alt="Cannabis leaf minimal"
                className="w-full h-[600px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent" />
            </div>
            
            {/* Floating Card */}
            <motion.div 
              className="absolute bottom-8 left-8 right-8 bg-card/95 backdrop-blur-xl rounded-2xl p-6 border border-primary/20 shadow-xl"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1, duration: 0.6 }}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full gradient-green flex items-center justify-center flex-shrink-0">
                  <span className="text-xl">🎉</span>
                </div>
                <div className="flex flex-col gap-0.5">
                  <p className="text-foreground">We've launched!</p>
                  <p className="text-sm text-muted-foreground">Join the UK's patient-led cannabis review platform</p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}